import { Filter, Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { memo } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import { Button } from '@/shared/components/ui';

interface TableSearchBarProps {
  /** Current search term */
  searchTerm: string;
  /** Callback when search term changes */
  onSearchChange: (term: string) => void;
  /** Placeholder text for search input */
  placeholder?: string;
  /** Whether to show filter button */
  showFilter?: boolean;
  /** Callback when filter button is clicked */
  onFilterClick?: () => void;
  /** Additional actions to show on the right side */
  actions?: ReactNode;
}

/**
 * Search bar component for TableCard matching the prototype design.
 */
export const TableSearchBar = memo(function TableSearchBar({
  searchTerm,
  onSearchChange,
  placeholder,
  showFilter = false,
  onFilterClick,
  actions,
}: TableSearchBarProps) {
  const { t } = useIntl();
  const searchPlaceholder = placeholder || t('common.search');
  const hasActions = actions !== undefined && actions !== null;
  const shouldShowRightSide = hasActions || showFilter;

  return (
    <>
      <div className="relative w-full md:flex-1">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          size={18}
        />
        <input
          type="text"
          placeholder={searchPlaceholder}
          className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all placeholder:text-muted-foreground"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      {shouldShowRightSide ? (
        <div className="flex gap-3 w-full md:w-auto md:shrink-0">
          {actions}
          {showFilter ? (
            <Button variant="outline" onClick={onFilterClick} className="flex items-center gap-2">
              <Filter size={16} />
              {t('common.filter')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </>
  );
});
